import React, { useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { getIn } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import { BaseTextFieldProps } from '@mui/material/TextField/TextField';
import { makeStyles } from 'tss-react/mui';
import classnames from 'classnames';
import { Property } from 'csstype';

import {
  getFormikValue,
  isDisplayShrink,
  onChangeWithInitReturn,
} from '../../../utils/formikFunctions';
import { FormikValues } from 'formik/dist/types';

const useStyles = makeStyles()((theme) => ({
  root: {
    '&:hover': {
      '& #close-icon': {
        visibility: 'visible',
      },
    },
  },
  closeIcon: {
    color: theme.palette.text.primary,
    height: 12,
    visibility: 'hidden',
    width: 12,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  viewModeWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface IFormTextField extends BaseTextFieldProps {
  id?: string;
  name: string;
  defaultValue?: string;
  className?: string;
  label: string;
  formik?: FormikValues;
  withClearButton?: boolean;
  InputLabelProps?: any;
  InputProps?: any;
  textAlign?: Property.TextAlign | undefined;
  viewMode?: boolean;
  onChange?: (e: any) => void;
}

const AppFormTextField: React.FC<IFormTextField> = ({
  id,
  name,
  label,
  formik,
  multiline = false,
  className = '',
  withClearButton = false,
  error = false,
  textAlign = 'left',
  inputProps,
  viewMode = false,
  ...props
}) => {
  const { classes } = useStyles();
  const value = useMemo(
    () => getFormikValue(getIn(formik?.values, name)),
    [formik?.values, name],
  );

  const [isAutofilled, setIsAutofilled] = useState(false);

  const handleAutofill = (e: React.AnimationEvent<HTMLInputElement>) => {
    if (e.animationName === 'mui-auto-fill') {
      setIsAutofilled(true);
    } else if (e.animationName === 'mui-auto-fill-cancel') {
      setIsAutofilled(false);
    }
  };

  if (viewMode)
    return (
      <Box className={classes.viewModeWrapper}>
        {label && <Typography variant="text-md-sb">{label}</Typography>}
        <Typography variant="text-md">{value as any}</Typography>
      </Box>
    );

  return (
    <TextField
      className={classnames(className, classes.root)}
      fullWidth
      label={label}
      multiline={multiline}
      id={id || name}
      name={name}
      margin="dense"
      value={value}
      onChange={onChangeWithInitReturn(name, formik, props.type === 'number')}
      onBlur={formik?.handleBlur}
      onAnimationStart={handleAutofill}
      InputLabelProps={
        formik
          ? {
              shrink:
                isDisplayShrink(getIn(formik?.values, name)) || isAutofilled,
            }
          : {}
      }
      error={
        error || (getIn(formik?.touched, name) && !!getIn(formik?.errors, name))
      }
      helperText={getIn(formik?.touched, name) && getIn(formik?.errors, name)}
      InputProps={
        withClearButton
          ? {
              endAdornment: (
                <>
                  {String(value).length > 0 && (
                    <CloseIcon
                      id="close-icon"
                      className={classes.closeIcon}
                      onClick={() => {
                        formik?.setFieldValue(name, '');
                      }}
                    />
                  )}
                </>
              ),
            }
          : {
              inputProps: {
                style: { textAlign },
                ...inputProps,
              },
            }
      }
      {...props}
    />
  );
};

export default AppFormTextField;
